import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { ProductOverviewStore } from '../';
import { formatDate } from '../../../utils/helper';
import { CategoryDetailStore, ProductDialogStore } from '../';
import { MultiSelect } from 'primereact/multiselect';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import {
    MatenFilter,
    MatenTable,
    MatenTableAction,
    MatenTableColumn,
    MatenChip,
} from '../../../shared';

const DeleteConfirmationDialog = observer(
    ({
        store,
        onConfirm,
        onCancel,
    }: {
        store: any;
        onConfirm: () => void;
        onCancel: () => void;
    }) => {
        const [confirmationText, setConfirmationText] = useState('');
        const searchWord: string = 'löschen';

        const handleConfirm = () => {
            if (confirmationText.toLowerCase() === searchWord) {
                setConfirmationText('');
                onConfirm();
            }
        };

        const handleCancel = () => {
            setConfirmationText('');
            onCancel();
        };

        const productName: string = store?.productDeleteConfirmation?.name || '';

        return (
            <Dialog
                visible={!!store.productDeleteConfirmation}
                modal
                header={'Produkt löschen bestätigen'}
                footer={() => (
                    <div>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={handleCancel}
                        />
                        <Button
                            label={'Löschen'}
                            icon="pi pi-check"
                            className="p-button-danger"
                            onClick={handleConfirm}
                            disabled={confirmationText.toLowerCase() !== searchWord}
                        />
                    </div>
                )}
                onHide={handleCancel}
            >
                <p>
                    Um das Produkt '{productName}' zu löschen, geben Sie bitte das Wort{' '}
                    <strong>{'"löschen"'}</strong> in das Feld unten ein.
                </p>
                <InputText
                    value={confirmationText}
                    onChange={e => setConfirmationText(e.target.value)}
                    placeholder={searchWord}
                    style={{ width: '100%' }}
                />
            </Dialog>
        );
    }
);

const ProductOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(ProductOverviewStore);
    }, [sdk]);

    // Define columns for products
    const columns: MatenTableColumn[] = [
        {
            field: 'image',
            header: '',
            sortable: false,
            style: { textAlign: 'center' },
            body: (rowData: any) =>
                rowData.image ? (
                    <img
                        src={rowData.image}
                        alt="Product"
                        style={{ width: '50px', height: '50px', borderRadius: '10%' }}
                    />
                ) : (
                    <i
                        className="pi pi-image"
                        style={{ fontSize: '2rem', textAlign: 'center' }}
                    />
                ),
        },
        {
            field: 'article_number',
            header: 'Artikel-Nummer',
            sortable: true,
        },
        {
            field: 'name',
            header: 'Name',
            sortable: true,
            body: (rowData: any) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {rowData.name}
                    {rowData.chargeable === 1 && (
                        <i
                            className="pi pi-euro"
                            style={{ fontSize: '0.8rem', color: '#E6007E', marginLeft: 8 }}
                            title="Kostenpflichtig"
                        ></i>
                    )}
                </div>
            ),
        },
        {
            field: 'category.name',
            header: 'Kategorie',
            sortable: false,
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created),
        },
        {
            field: 'updated',
            header: 'Aktualisiert',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.updated),
        },
    ];

    // Define actions for products
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Produkt bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.openDialog(ProductDialogStore, { product_id: rowData.id });
            },
            testId: 'edit-product-btn',
        },
        {
            icon: 'pi pi-trash',
            tooltip: 'Produkt löschen',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.currentStore.onDeleteProduct(rowData); // Call the delete logic from the store
            },
            testId: 'delete-product-btn',
            className: 'p-button-danger',
        },
    ];

    // Additional header content
    const headerContent = (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
                label={'Neu'}
                icon="pi pi-plus"
                aria-label="Filter"
                onClick={() => sdk.openDialog(ProductDialogStore, {})}
                style={{ marginRight: '0.5rem' }}
                data-testid="add-product-btn"
            />
            <Button
                label="Kategorien"
                icon="pi pi-cog"
                aria-label="Filter"
                onClick={() => sdk.openDialog(CategoryDetailStore, {})}
                data-testid="manage-categories-btn"
            />
        </div>
    );

    const tableFilter: MatenFilter[] = [
        {
            title: 'Kategorien wählen',
            property: 'filterCategories',
            removeFilter: (store: any) => {
                store.removeAllFilterCategories();
            },
            // chipContent: (filter: any) => filter ? `Kategorien: ${filter?.map((cat: any) => cat.name).join(', ')}` || '' : '',
            filterComponent: (store: any) => (
                <MultiSelect
                    value={store?.filterCategories || []} // Selected categories
                    options={store?.available_categories || []} // Available categories
                    onChange={(e: any) => store.applyFilterCategory(e.value)}
                    onShow={() => store?.fetchAvailableCategories()} // Lazy load categories when focused
                    onHide={() => store?.applyFilterCategory()} // Apply filter when closed
                    placeholder="Kategorien auswählen"
                    optionLabel="name"
                    selectionLimit={3} // Max labels to display
                    display="chip" // Display selected categories as chips
                    style={{ width: '100%' }}
                    data-testid="category-filter-multiselect"
                />
            ),
        },
    ];

    const tableAdditionalChips: MatenChip[] =
        sdk.currentStore?.filterCategories?.map((category: any) => ({
            label: `${category.name}`,
            onRemove: (store: any) => store.removeFilterCategory(category),
        })) || [];

    // Row className for products without category
    const rowClassName = (data: any) => {
        return !data.category?.name ? 'red-row' : '';
    };

    return (
        <>
            <MatenTable
                data={sdk.currentStore?.allProducts || []}
                store={sdk.currentStore}
                columns={columns}
                actions={actions}
                filter={tableFilter}
                additionalChips={tableAdditionalChips}
                search={{
                    placeholder: 'Produkte durchsuchen',
                }}
                emptyMessage="Keine Produkte gefunden"
                entity={{ singular: 'Produkt', plural: 'Produkte' }}
                testId={'products-overview'}
                toolbarContent={headerContent}
                pagination={{ rows: 30 }}
                rowClassName={rowClassName}
            />
            <DeleteConfirmationDialog
                store={sdk.currentStore}
                onConfirm={() => sdk.currentStore.onDeleteProductConfirm()}
                onCancel={() => sdk.currentStore.onDeleteProductCancel()}
            />
        </>
    );
});

export default ProductOverview;
